import { ReactNode, useEffect, useState } from 'react';
import ClipLoader from 'react-spinners/ClipLoader';
import i18n from './i18n';

const hasActiveTranslations = () =>
    i18n.isInitialized && i18n.hasResourceBundle(i18n.language, 'messages');

// Holds rendering until the active language's `messages` bundle is loaded.
// A deploy invalidates the localStorage translation cache, so the JSON is
// re-fetched async; without this gate, `fallbackLng: 'en_US'` makes the UI
// flash English until that fetch completes.
export default function I18nGate({ children }: { children: ReactNode }) {
    const [ready, setReady] = useState(hasActiveTranslations);

    useEffect(() => {
        if (ready) return;
        const update = () => {
            if (hasActiveTranslations()) setReady(true);
        };
        i18n.on('initialized', update);
        i18n.on('loaded', update);
        i18n.on('languageChanged', update);
        update();
        // Safety valve: never block forever if the fetch fails — fall back to
        // whatever i18next has rather than spinning indefinitely.
        const timeout = window.setTimeout(() => setReady(true), 8000);
        return () => {
            i18n.off('initialized', update);
            i18n.off('loaded', update);
            i18n.off('languageChanged', update);
            window.clearTimeout(timeout);
        };
    }, [ready]);

    if (!ready) {
        return (
            <div className="fixed inset-0 flex items-center justify-center bg-gray-50">
                <ClipLoader color="#64BE78" size={50} />
            </div>
        );
    }

    return <>{children}</>;
}
